import { trackPromptHeroSubmitted, type PromptHeroHandOff } from "app/utils/mixpanel_tracking"
import { savePendingPrompt, type PendingPromptSource } from "app/utils/pending_prompt_storage"
import { fitPromptHeight, wirePromptField } from "app/utils/prompt_field"
import { withPromptHash } from "app/utils/prompt_hash_hand_off"
import { formatPromptCount, isPromptAtLimit, showPromptCount } from "app/utils/prompt_length"

// The only signed-in signal that works here: the signed-out HTML of /explore and
// the home is CDN-cached and identical for everyone, but signed-in visitors
// always carry the auth_cdn_cache_key cookie (see concerns/authentication.rb).
// If the cookie is somehow missing, the signed-out path still degrades safely —
// /join redirects an authenticated visitor onward and the prompt stays parked
// for the /create intro.
const signedIn = () => document.cookie.split("; ").some(entry => entry.startsWith("auth_cdn_cache_key="))

// Wires one prompt-capture hero (the /explore hero, the signed-out home hero).
// The root carries data-create-path, data-join-path, data-signup-path and
// optionally data-prompt-source; inside it: .prompt-hero-input,
// .prompt-hero-submit and optionally .prompt-hero-count and .prompt-hero-examples chips.
export const PromptHero = (selector = ".explore-prompt-hero") => {
  const hero = document.querySelector<HTMLElement>(selector)
  if (!hero) return

  const input = hero.querySelector<HTMLTextAreaElement>(".prompt-hero-input")
  const submit = hero.querySelector<HTMLButtonElement>(".prompt-hero-submit")
  const count = hero.querySelector<HTMLElement>(".prompt-hero-count")
  const field = hero.querySelector<HTMLElement>(".prompt-hero-field")
  const createPath = hero.dataset.createPath
  const joinPath = hero.dataset.joinPath
  const signupPath = hero.dataset.signupPath
  const source = (hero.dataset.promptSource || "explore_prompt_hero") as PendingPromptSource
  if (!input || !submit || !createPath || !joinPath || !signupPath) return

  const updateCount = () => {
    const { length } = input.value
    field?.classList.toggle("is-at-limit", isPromptAtLimit(length))
    if (!count) return
    count.textContent = formatPromptCount(length)
    count.hidden = !showPromptCount(length)
    count.classList.toggle("is-at-limit", isPromptAtLimit(length))
  }

  const teardownField = wirePromptField(field, input)

  const startBuilding = () => {
    const prompt = input.value.trim()
    const isSignedIn = signedIn()
    const handOff = (url: string, method: PromptHeroHandOff) => {
      trackPromptHeroSubmitted({ source, promptLength: prompt.length, isSignedIn, handOff: method })
      window.location.assign(url)
    }

    // Served from another origin (the ifttt.ai homepage), this page sees
    // neither the auth cookie nor ifttt.com's localStorage, so the prompt rides
    // in the fragment of the absolute join URL. /join sends a signed-in visitor
    // on to its return_to, and every ifttt.com page parks the fragment.
    if (new URL(joinPath, window.location.href).origin !== window.location.origin) {
      if (prompt.length === 0) {
        handOff(new URL(createPath, joinPath).href, "none")
      } else {
        handOff(withPromptHash(joinPath, prompt, source), "hash")
      }
      return
    }

    // An empty submit is a plain CTA. The signup path has no return_to, because
    // any return_to makes signup skip the free-trial onboarding.
    if (prompt.length === 0) {
      handOff(isSignedIn ? createPath : signupPath, "none")
      return
    }

    // The prompt travels via localStorage on both paths so it never lands in
    // a URL; the one-time ?autostart= token marks the already-authenticated
    // fast path (and only the latest hand-off's token can auto-send). Signed
    // out, the join flow's return_to brings the visitor back to /create,
    // where the parked prompt pre-fills the intro for an explicit send.
    // When the write fails (localStorage full or blocked), a signed-in prompt
    // rides in the fragment and pre-fills /create without auto-sending. A
    // fragment cannot survive the multi-page signup, so signed out it is lost.
    const token = savePendingPrompt(prompt, source)
    if (!isSignedIn) {
      handOff(joinPath, token ? "storage" : "lost")
    } else if (token) {
      handOff(`${createPath}?autostart=${encodeURIComponent(token)}`, "storage")
    } else {
      handOff(withPromptHash(createPath, prompt, source), "hash")
    }
  }

  submit.addEventListener("click", startBuilding)
  input.addEventListener("input", () => {
    updateCount()
    fitPromptHeight(input)
  })
  input.addEventListener("keydown", event => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      startBuilding()
    }
  })
  updateCount()

  hero.querySelectorAll<HTMLButtonElement>(".prompt-hero-examples button").forEach(chip => {
    chip.addEventListener("click", () => {
      input.value = chip.textContent?.trim() ?? ""
      updateCount()
      fitPromptHeight(input)
      startBuilding()
    })
  })

  // Both pages wire the hero once per full load, so nothing calls this today;
  // it is here for a caller that swaps the hero without a navigation.
  return teardownField
}
